import React, { isValidElement } from "react";
import ReactDOMServer from "react-dom/server";
import TableCell from "react-base-table/es/TableCell";
import { renderElement, getValue } from "react-base-table/es/utils";
import { ExportToCsv } from "export-to-csv-fix-source-map";

import { isFunction, isString } from "./utils";
import { isTableBusy } from "./actions";

const exportTableToCsv = (
  columns,
  data,
  csvFileName,
  onExportCSV,
  fireAction,
  onToastHandler,
  enableCSVToastMessage
) => {
  let rowsData = [...data];
  let totalRows = data.length;
  let totalChunkRows = [];

  const headers = columns.map(col => {
    return isString(col.csvTitle)
      ? col.csvTitle
      : isString(col.title)
      ? col.title
      : "";
  });

  function extractContent(value) {
    const div = document.createElement("div");
    div.innerHTML = value;
    const text = div.textContent;
    return text;
  }

  const sanitizeCellValue = value => {
    const injectionVulnerableRegex = /^[=@+-]/;

    const sanitizedCell =
      typeof value === "string" && injectionVulnerableRegex.test(value)
        ? `'${value}`
        : value;

    return sanitizedCell;
  };

  const getCSVData = dataValue => {
    return dataValue.map((rowData, rowIndex) => {
      const row = {};
      columns.forEach((column, columnIndex) => {
        const { dataGetter, dataKey, cellRenderer, key, getCsvValue } = column;
        const cellData = dataGetter
          ? dataGetter({ columns, column, columnIndex, rowData, rowIndex })
          : getValue(rowData, dataKey);
        const cellProps = {
          cellData,
          columns,
          column,
          columnIndex,
          rowData,
          rowIndex
        };
        const cell = renderElement(
          getCsvValue || cellRenderer || <TableCell />,
          cellProps
        );

        row[key] = sanitizeCellValue(
          isValidElement(cell)
            ? extractContent(ReactDOMServer.renderToString(cell))
            : cell
        );
      });

      return row;
    });
  };

  const options = {
    fieldSeparator: ",",
    quoteStrings: '"',
    decimalSeparator: ".",
    showLabels: true,
    useTextFile: false,
    useBom: true,
    filename: csvFileName,
    headers
  };

  const csvExporter = new ExportToCsv(options);

  function runTaskQueue(deadline) {
    while (
      (deadline.timeRemaining() > 0 || deadline.didTimeout) &&
      rowsData.length
    ) {
      // make list into smaller chunks
      for (let i = 0; i < rowsData.length; i += 1000) {
        if (deadline.timeRemaining() > 0) {
          const chunk = rowsData.splice(i, 1000);
          totalChunkRows = [...totalChunkRows, ...getCSVData(chunk)];
        }
      }

      if (totalRows === totalChunkRows.length) {
        csvExporter.generateCsv(totalChunkRows);
      }
    }
    if (rowsData.length > 0) {
      requestIdleCallback(runTaskQueue);
    }
  }

  function handleIdleDeadline(totalRowsData = data) {
    rowsData = [...totalRowsData];
    const intervalID = setInterval(() => {
      if (rowsData.length > 0) {
        const chunk = rowsData.splice(0, 1000);
        totalChunkRows = [...totalChunkRows, ...getCSVData(chunk)];
      } else {
        clearInterval(intervalID);
        if (totalRowsData.length === totalChunkRows.length) {
          csvExporter.generateCsv(totalChunkRows);
        }
      }
    }, 300);
  }

  if (isFunction(onExportCSV)) {
    const promise = onExportCSV();
    if (promise instanceof Promise) {
      promise
        .then(csvResults => {
          rowsData = [...csvResults];
          totalRows = rowsData.length;
          onToastHandler(enableCSVToastMessage);
          if (window.requestIdleCallback) {
            requestIdleCallback(runTaskQueue);
          } else {
            // Fallback for browsers which don't support requestIdleCallback
            setTimeout(() => {
              handleIdleDeadline(rowsData);
            }, 300);
          }
        })
        .catch(error => {
          throw new Error(error);
        })
        .finally(() => {
          fireAction(isTableBusy(false));
        });
    }
  } else {
    onToastHandler(enableCSVToastMessage);
    if (window.requestIdleCallback) {
      requestIdleCallback(runTaskQueue);
    } else {
      // Fallback for browsers which don't support requestIdleCallback
      setTimeout(() => {
        handleIdleDeadline();
      }, 300);
    }

    fireAction(isTableBusy(false));
  }
};

export default exportTableToCsv;
